import { lazy, Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Marquee } from '@/components/layout/Marquee';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { CartDrawer } from '@/features/cart/CartDrawer';
import { Toaster } from '@/components/ui/toaster';
import { routeChrome } from './routeChrome';
import { initTracking, trackEvent } from '@/features/tracking/tracker';

// Lazy-load the chat: it pulls in the quote engine + all catalog data, none of
// which is needed for first paint. Keeps it out of the entry chunk.
const ChatWidget = lazy(() =>
  import('@/features/support/ChatWidget').then((m) => ({ default: m.ChatWidget })),
);

export function AppShell() {
  const { pathname } = useLocation();
  const chrome = routeChrome(pathname);

  // Visitor analytics: register/refresh the guest (geo lookup) once, then log
  // a page_view per route change. Fire-and-forget — never blocks rendering.
  useEffect(() => {
    initTracking();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    trackEvent('page_view', { path: pathname });
  }, [pathname]);

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      {chrome.marquee && <Marquee />}
      <Header />
      <main className="flex-1">
        <Suspense fallback={<PageLoading />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
      <CartDrawer />
      {chrome.chat && (
        <Suspense fallback={null}>
          <ChatWidget />
        </Suspense>
      )}
      <Toaster />
    </div>
  );
}

function PageLoading() {
  return (
    <div className="container flex min-h-[50vh] items-center justify-center text-muted-foreground">
      <span className="animate-pulse">Loading…</span>
    </div>
  );
}
